import {
  DRAFT_PREFIX,
  FORM_DRAFT_MAX_AGE_MS,
  notifyDraftsChanged,
} from "@/lib/formPersistence/formDraftStorage";

/**
 * Work left half-done, listed in the drafts tray: ticket replies (saved per
 * ticket in this browser) and invoices autosaved as Draft.
 */
export type TrayDraft =
  | { kind: "ticket-reply"; ticketId: string; savedAt: number }
  | { kind: "invoice"; invoiceId: string; savedAt: number }
  | { kind: "sms"; conversationId: string; savedAt: number };

export const smsDraftStorageKey = (
  memberId: string | number,
  conversationId: string | number,
) => `sms:${memberId}:${conversationId}`;

const INVOICE_TRAY_KEY = "nomi_invoice_draft_tray";

const htmlHasText = (html: string) =>
  html
    .replace(/<[^>]*data-lbs-signature[\s\S]*$/i, "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .trim().length > 0;

/** Ticket reply drafts of this member that still have something written. */
export const listTicketReplyDrafts = (
  memberId: string | number,
): TrayDraft[] => {
  if (typeof localStorage === "undefined") return [];
  const prefix = `${DRAFT_PREFIX}ticket-reply:${memberId}:`;
  const drafts: TrayDraft[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (!key?.startsWith(prefix)) continue;
    try {
      const envelope = JSON.parse(localStorage.getItem(key) ?? "") as {
        values?: { bodyHtml?: string; internalNoteText?: string };
        savedAt?: number;
      };
      const savedAt = Number(envelope.savedAt) || 0;
      if (Date.now() - savedAt > FORM_DRAFT_MAX_AGE_MS) continue;
      const values = envelope.values ?? {};
      const hasContent =
        htmlHasText(values.bodyHtml ?? "") ||
        Boolean(values.internalNoteText?.trim());
      if (!hasContent) continue;
      drafts.push({
        kind: "ticket-reply",
        ticketId: key.slice(prefix.length),
        savedAt,
      });
    } catch {
      // Unreadable entry: skip it.
    }
  }
  return drafts;
};

const readInvoiceTray = (): Array<{ id: string; savedAt: number }> => {
  try {
    const rows = JSON.parse(localStorage.getItem(INVOICE_TRAY_KEY) ?? "[]");
    return Array.isArray(rows)
      ? rows.filter(
          (row) =>
            row?.id != null &&
            Date.now() - Number(row.savedAt) <= FORM_DRAFT_MAX_AGE_MS,
        )
      : [];
  } catch {
    return [];
  }
};

const writeInvoiceTray = (rows: Array<{ id: string; savedAt: number }>) => {
  try {
    localStorage.setItem(INVOICE_TRAY_KEY, JSON.stringify(rows));
    notifyDraftsChanged();
  } catch {
    // Storage blocked: the invoice is still saved as Draft.
  }
};

/** An invoice was autosaved as Draft and is still being worked on. */
export const trackInvoiceDraft = (invoiceId: string | number) => {
  const id = String(invoiceId);
  writeInvoiceTray([
    { id, savedAt: Date.now() },
    ...readInvoiceTray().filter((row) => row.id !== id),
  ]);
};

/** Saved, sent or dismissed: leave the tray. */
export const untrackInvoiceDraft = (invoiceId: string | number) => {
  const id = String(invoiceId);
  writeInvoiceTray(readInvoiceTray().filter((row) => row.id !== id));
};

export const listInvoiceDrafts = (): TrayDraft[] =>
  readInvoiceTray().map((row) => ({
    kind: "invoice",
    invoiceId: row.id,
    savedAt: row.savedAt,
  }));

/** Unsent SMS text of this member, per conversation. */
export const listSmsDrafts = (memberId: string | number): TrayDraft[] => {
  if (typeof localStorage === "undefined") return [];
  const prefix = `${DRAFT_PREFIX}sms:${memberId}:`;
  const drafts: TrayDraft[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (!key?.startsWith(prefix)) continue;
    try {
      const envelope = JSON.parse(localStorage.getItem(key) ?? "") as {
        values?: { body?: string };
        savedAt?: number;
      };
      const savedAt = Number(envelope.savedAt) || 0;
      if (Date.now() - savedAt > FORM_DRAFT_MAX_AGE_MS) continue;
      if (!envelope.values?.body?.trim()) continue;
      drafts.push({
        kind: "sms",
        conversationId: key.slice(prefix.length),
        savedAt,
      });
    } catch {
      // Unreadable entry: skip it.
    }
  }
  return drafts;
};
